import {
  CompetenceBaseTreeEx,
  CompetenceBaseWithChildrenEx,
} from "@/server/backend-api";
import { DUMMY_TREE } from "@/utils/competenceTree";

// Navigation path: array of indices to traverse tree
export type CompetenceNavigationPath = number[];

// Build O(1) competence ID index mapping IDs to their navigation path in tree
export function buildCompetenceIndex(
  tree: CompetenceBaseTreeEx
): Map<string, CompetenceNavigationPath> {
  const index = new Map<string, CompetenceNavigationPath>();

  const traverse = (
    node: CompetenceBaseWithChildrenEx,
    path: CompetenceNavigationPath
  ) => {
    index.set(node.id, path);
    for (let i = 0; i < node.children.length; i++) {
      traverse(node.children[i], [...path, i]);
    }
  };

  const roots = Object.values(tree);
  for (let i = 0; i < roots.length; i++) {
    traverse(roots[i], [i]);
  }

  return index;
}

// Navigate tree using path to get node
export function getNodeByPath(
  tree: CompetenceBaseTreeEx,
  path: CompetenceNavigationPath
): CompetenceBaseWithChildrenEx | undefined {
  const roots = Object.values(tree);
  let current: CompetenceBaseWithChildrenEx | undefined = roots[path[0]];

  for (let i = 1; i < path.length; i++) {
    if (!current) return undefined;
    current = current.children[path[i]];
  }

  return current;
}

// Utilities object that bundles tree navigation
export type CompetenceTreeUtils = {
  index: Map<string, CompetenceNavigationPath>;
  getNodeById: (id: string) => CompetenceBaseWithChildrenEx | undefined;
};

// Create competence tree utilities
export function createCompetenceTreeUtils(
  tree: CompetenceBaseTreeEx
): CompetenceTreeUtils {
  const index = buildCompetenceIndex(tree);

  return {
    index,
    getNodeById: (id: string) => {
      const path = index.get(id);
      return path ? getNodeByPath(tree, path) : undefined;
    },
  };
}

// Default utilities
export const defaultCompetenceTreeUtils = createCompetenceTreeUtils(DUMMY_TREE);
